import type { BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import type { ChemistryControllerHost } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { _useMemo, getCurrentComponent, stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { globalLanguageSubject } from '@adornis/translation-core/client/languageSubject.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { Signal } from '@lit-labs/preact-signals';
import { PartType } from 'lit/async-directive.js';
import { state } from 'lit/decorators.js';

export type BuchungsheldLitElementHost = ChemistryControllerHost & {
  _translation: TranslationController<BHTranslationDictionary>;
};

export const institutionPageActionConfig = new Signal<Array<Action>>([]);

export type Action = { icon: string; text?: string; color?: 'primary' | 'danger'; callback: () => void };

export const _actionProvider = new Map<any, Record<string, Action>>();
export const notifyActionChange = () => {
  const allCurrentActions = [] as Array<Action>;
  for (const compActions of _actionProvider.values()) allCurrentActions.push(...Object.values(compActions));
  institutionPageActionConfig.value = allCurrentActions;
};

// for use functional lit
export const useExposeActions = () => {
  const getHost = () => {
    const component = getCurrentComponent();
    const part = component.instance.part;
    if (!part) return undefined;
    try {
      if (part.type !== PartType.CHILD) throw new Error('partType is not supported: ' + part.type);
      return (part as any).parentNode;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  };

  // createHook
  _useMemo(hook => {
    const cleanup = () => {
      const host = getHost();
      _actionProvider.delete(host);
      notifyActionChange(); // notify removal of action
    };

    return [void 0, cleanup];
  }, []);

  return {
    expose: (key: string, action: Action) => {
      const host = getHost();
      if (!host) return;
      const componentActions =
        _actionProvider.get(host) ??
        (() => {
          const actions = {} as Record<string, Action>;
          _actionProvider.set(host, actions);
          return actions;
        })();
      componentActions[key] = action;
      notifyActionChange(); // notify new action
    },
  };
};

export function getExposedActionsOfElement(host: any, recurse: boolean) {
  if (recurse) throw new Error('not implemented');
  return _actionProvider.get(host) ?? {};
}

export class BuchungsheldLitElement extends ChemistryLitElement {
  @state() readonly _translation = new TranslationController<any>(this);

  e = (err: any) => {
    const errMessage = (() => {
      if (err?.extensions?.translationKey) {
        return this._translation.translate('errors', err.extensions.translationKey);
      }

      const errCode = err?.message ?? 'unknown';
      return this._translation.translate('err', errCode);
    })();

    XSnackbar.show(errMessage);
  };

  override disconnectedCallback(): void {
    _actionProvider.delete(this);
    notifyActionChange();
  }

  exposeAction(key: string, action: Action) {
    const componentActions =
      _actionProvider.get(this) ??
      (() => {
        const actions = {} as Record<string, Action>;
        _actionProvider.set(this, actions);
        return actions;
      })();

    // FIXME: this should not be necessary
    // clenup orphans
    for (const [host, actions] of _actionProvider.entries()) {
      if (host.isConnected) continue;
      console.warn('found orphaned action provider; removing it now', { host, actions });
      _actionProvider.delete(host);
    }

    // notify new action
    componentActions[key] = action;
    notifyActionChange();
  }
}

// translation
export const trn = stateful<[string, string]>(translation => {
  const language = useObservable(() => globalLanguageSubject, [42]);
  if (!language) return translation[0];
  return translation[language === 'de' ? 0 : 1];
});
